import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CalendarDays, Clock } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import projectsHero from "@/assets/mckay/roof-replacement.webp.asset.json";
import img1 from "@/assets/blog/blog-1.jpg.asset.json";
import img2 from "@/assets/blog/blog-2.jpg.asset.json";
import img3 from "@/assets/blog/blog-3.jpg.asset.json";
import img4 from "@/assets/blog/blog-4.jpg.asset.json";
import img5 from "@/assets/blog/blog-5.png.asset.json";
import img6 from "@/assets/blog/blog-6.png.asset.json";

const PAGE_URL = "https://mckay.steadyautogrowthdemo.com/recent-work";

export const Route = createFileRoute("/recent-work")({
  head: () => ({
    meta: [
      { title: "Recent Roofing & Solar Projects in Southern California | McKay" },
      { name: "description", content: "Recent roofing and solar projects completed across Southern California by McKay Roofing & Solar - tear-offs, TPO and torch-down flat roofs, shingle replacements and solar installs." },
      { name: "keywords", content: "Southern California roofing projects, roof replacement Southern California, TPO roofing, torch down roofing, solar install Southern California" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "publisher", content: "McKay Roofing & Solar" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Recent Roofing & Solar Projects in Southern California | McKay" },
      { property: "og:description", content: "Job-by-job look at roofing and solar work McKay Roofing & Solar recently finished across Southern California." },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Recent Roofing & Solar Projects in Southern California | McKay" },
      { name: "twitter:description", content: "Tear-offs, flat roof systems, shingle replacements and solar installs - documented job by job." },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
  }),
  component: RecentWorkPage,
});

type Project = {
  title: string;
  excerpt: string;
  image: string;
  category: string;
  date: string;
  read: string;
};

const projects: Project[] = [
  {
    title: "Gravel Roof Tear-Off, New TPO Roof + Solar Install in Southern California",
    excerpt: "A full gravel tear-off, a new 60 mil TPO system and a solar install on the same Southern California building - here is how we sequenced the work so the panels landed on a roof with decades left in it.",
    image: img1.url,
    category: "Projects",
    date: "May 3, 2026",
    read: "3 min read",
  },
  {
    title: "Tear-Off Cap Sheet & New Torch-Down Roof Installation in Southern California",
    excerpt: "We stripped a tired cap sheet down to clean decking and installed a new torch-down system backed by a 20-year manufacturer warranty. A look at the details that decide whether a flat roof lasts.",
    image: img2.url,
    category: "Projects",
    date: "May 3, 2026",
    read: "3 min read",
  },
  {
    title: "Ocean Beach Flat Roof Replacement on Bacon Street",
    excerpt: "Salt air, coastal fog and an aging flat roof a few blocks from the sand. Full tear-off, new torch-down system, and the drainage fixes that keep water moving off the roof instead of sitting on it.",
    image: img3.url,
    category: "Projects",
    date: "April 2, 2026",
    read: "5 min read",
  },
  {
    title: "City Heights Roof Replacement | 45th Street Southern California Project",
    excerpt: "A complete tear-off and new asphalt shingle roof on a 45th Street home in City Heights, finished on schedule with the driveway swept clean at the end of every day.",
    image: img4.url,
    category: "Projects",
    date: "April 2, 2026",
    read: "5 min read",
  },
  {
    title: "Southern California Roof Replacement | Good Life Property",
    excerpt: "CertainTeed shingles on the sloped sections and a silicone coating over the existing torch-down flat roof - a two-system approach for a property manager who needed one dependable result.",
    image: img5.url,
    category: "Projects",
    date: "March 13, 2026",
    read: "4 min read",
  },
  {
    title: "Cuyamaca Rancho State Park Synthetic Tile Roofing",
    excerpt: "Synthetic tile roofing installed up in the mountains at Cuyamaca Rancho State Park, chosen for fire resistance and for holding up to swings in temperature the coast never sees.",
    image: img6.url,
    category: "Projects",
    date: "February 17, 2026",
    read: "2 min read",
  },
];

function ProjectCard({ post }: { post: Project }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition duration-300 hover:-translate-y-1 hover:border-accent hover:shadow-xl">
      <div className="relative aspect-[16/10] w-full overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 rounded-full bg-primary/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground backdrop-blur">
          {post.category}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            {post.date}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            {post.read}
          </span>
        </div>
        <h3 className="mt-3 font-display text-lg font-bold text-balance text-primary transition group-hover:text-accent">
          {post.title}
        </h3>
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">{post.excerpt}</p>
        <div className="mt-auto pt-5">
          <Button asChild size="sm" variant="outline" className="btn-shine">
            <Link to="/contact" className="inline-flex items-center gap-1.5">
              Read More <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </article>
  );
}

function RecentWorkPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={projectsHero.url} alt="Finished roof replacement on a Southern California home" />
          <div className="relative mx-auto flex min-h-[60vh] max-w-5xl flex-col justify-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
            <h1 className="font-display text-4xl font-extrabold text-balance sm:text-5xl lg:text-6xl">
              Recent roofing &amp; solar work
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">
              Tear-offs, flat roof systems, shingle replacements and solar installs - documented job by job across
              Southern California.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-lift btn-shine">
                <Link to="/contact">Get My Free Estimate</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="btn-shine border-white/50 bg-white/10 text-white hover:bg-white hover:text-primary"
              >
                <Link to="/blog">Read the blog</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <ProjectCard key={p.title} post={p} />
            ))}
          </div>
        </section>

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
